/**
 * Token Manager
 * 负责 JWT Token 的存储、读取和清除
 * 支持 localStorage 和 cookie 两种模式
 * 使用 localStorage 存储 JSON 格式的 TokenInfo
 */

const TOKEN_STORAGE_KEY = 'skeleton_token';

// 认证配置（从 app profile 获取）
let authStorage: 'localStorage' | 'cookie' = 'localStorage';

/**
 * 设置认证存储模式
 * 在 app.tsx 初始化时调用
 */
export function setAuthStorage(mode: 'localStorage' | 'cookie'): void {
  authStorage = mode;
}

/**
 * 获取当前认证存储模式
 */
export function getAuthStorage(): 'localStorage' | 'cookie' {
  return authStorage;
}

/**
 * 判断是否为 cookie 模式
 */
export function isCookieMode(): boolean {
  return authStorage === 'cookie';
}

export interface TokenInfo {
  token: string;
  expires_at: number; // Unix 时间戳（秒）
  user_id: string;
  username: string;
  nickname: string;
  picture: string;
  /** 多租户支持：租户 ID (Requirement 12.1) */
  tenant_id?: string;
  /** 多租户支持：应用 ID (Requirement 12.1) */
  app_id?: string;
}

/**
 * 获取 Token 字符串
 * Cookie 模式下返回 null（由浏览器自动携带）
 */
export function getToken(): string | null {
  if (isCookieMode()) {
    return null;
  }
  const info = getTokenInfo();
  return info?.token ?? null;
}

/**
 * 获取完整的 TokenInfo 对象
 * 处理 JSON 解析错误，返回 null
 * Cookie 模式下返回 null
 */
export function getTokenInfo(): TokenInfo | null {
  if (isCookieMode()) {
    return null;
  }
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // 基本校验：确保 token 字段存在
    if (!parsed || typeof parsed.token !== 'string') return null;
    return parsed as TokenInfo;
  } catch {
    return null;
  }
}

/**
 * 存储 TokenInfo
 * Cookie 模式下跳过（由后端设置 cookie）
 */
export function setTokenInfo(info: TokenInfo): void {
  if (isCookieMode()) {
    return;
  }
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(info));
}

/**
 * 清除 Token
 * Cookie 模式下跳过（由后端控制过期）
 */
export function removeToken(): void {
  if (isCookieMode()) {
    return;
  }
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * 检查 Token 是否已过期
 * 如果无法获取 TokenInfo，视为已过期
 * Cookie 模式下返回 false（由服务端控制）
 */
export function isTokenExpired(): boolean {
  if (isCookieMode()) {
    return false;
  }
  const info = getTokenInfo();
  if (!info) return true;
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return nowInSeconds >= info.expires_at;
}

/**
 * 头像 URL 补全
 * 后端返回的 picture 可能是相对路径（如 /images/avatar/xxx），
 * 需要补全 API 前缀；如果已经是完整 http(s) 地址则直接返回。
 */
const AUTH_API_PREFIX = '/api/auth';

export function resolveAvatarUrl(
  picture: string | undefined | null,
): string | undefined {
  if (!picture) return undefined;
  if (/^https?:\/\//i.test(picture)) return picture;
  // 已经包含前缀则直接返回
  if (picture.startsWith(AUTH_API_PREFIX)) return picture;
  // 确保拼接时不会出现双斜杠
  const path = picture.startsWith('/') ? picture : `/${picture}`;
  return `${AUTH_API_PREFIX}${path}`;
}
